import React from 'react';
import { useTheme } from '@mui/material';

import Description from 'components/CareSummary/Description';
import Empty from 'components/CareSummary/Empty';
import Title from 'components/CareSummary/Title';
import { LineChart } from 'components/Graphs/LineChart';
import { Container } from 'shared-components/Container';
import { useGetEHRBloodPressureQuery } from 'store/api/pkd/endpoints/graphs';
import { useGetCurrentParticipantQuery } from 'store/api/pkd/endpoints/user';
import { formatDate, groupDataByDate } from 'utils/date';
import { ChartGraphSkeleton } from '../ChartSkeleton';

const BloodPressureControl = () => {
  const theme = useTheme();
  const { data: person, isLoading: isPersonLoading } =
    useGetCurrentParticipantQuery();
  const personId = person?.id;
  let {
    systolicData = {},
    diastolicData = {},
    isFetching: isEHRDataLoading,
  } = useGetEHRBloodPressureQuery(
    { personId },
    {
      skip             : !personId,
      selectFromResult : ({ data, isLoading, isFetching }) => {
        const bloodPressures = data?.data?.bloodPressures?.items ?? [];
        const systolicData = groupDataByDate(
          bloodPressures
            .map((bloodPressure) => ({
              value : bloodPressure.systolic,
              date  : formatDate(bloodPressure.effectiveDatetime, 'YYYY-MM'),
            }))
            .sort((a, b) => a.date.localeCompare(b.date))
        );
        const diastolicData = groupDataByDate(
          bloodPressures
            .map((bloodPressure) => ({
              value : bloodPressure.diastolic,
              date  : formatDate(bloodPressure.effectiveDatetime, 'YYYY-MM'),
            }))
            .sort((a, b) => a.date.localeCompare(b.date))
        );
        return {
          systolicData,
          diastolicData,
          isLoading,
          isFetching,
        };
      },
    }
  );
  const isLoading = isPersonLoading || isEHRDataLoading;

  // Graph option
  const graphDate = Object.keys(systolicData).map((date) =>
    formatDate(date, 'MMM YYYY')
  );
  const graphValues = [
    ...Object.values(systolicData),
    ...Object.values(diastolicData),
  ];
  const offset = 20;
  const minValue = Math.min(...graphValues, 60) - offset;
  const maxValue = 240;
  const ehrGraphOptions = {
    xAxis : {
      type : 'category',
      data : graphDate,
    },
    grid : {
      y2 : 90,
    },
    legend : {
      data : [
        'Systolic',
        'Diastolic',
        'HALT study recommended (95/60 to 110/75 Hg)',
      ],
      textStyle : {
        fontFamily : 'Montserrat',
      },
      icon   : 'circle',
      bottom : '5%',
      left   : '10%',
    },
    yAxis : {
      type : 'value',
      min  : minValue,
      max  : maxValue,
    },
    series : [
      {
        name      : 'HALT study recommended (95/60 to 110/75 Hg)',
        type      : 'line',
        itemStyle : { color: theme.palette.primary.greenPKD },
        lineStyle : { color: theme.palette.primary.greenPKD },
        markLine  : {
          silent    : true,
          symbol    : [],
          lineStyle : {
            color : theme.palette.primary.greenPKD,
            width : 2,
          },
          label : {
            color : theme.palette.primary.greenPKD,
          },
          data : [
            {
              yAxis : 60,
            },
            {
              yAxis : 110,
            },
          ],
        },
      },
      {
        name      : 'Systolic',
        data      : Object.values(systolicData),
        type      : 'line',
        itemStyle : { color: theme.palette.primary.blue1 },
        lineStyle : { color: theme.palette.primary.blue1 },
      },
      {
        name      : 'Diastolic',
        data      : Object.values(diastolicData),
        type      : 'line',
        itemStyle : { color: theme.palette.primary.orange },
        lineStyle : { color: theme.palette.primary.orange },
      },
    ],
  };

  const content = isLoading
    ? (
      <ChartGraphSkeleton />
    )
    : (
      <>
        {Object.keys(systolicData).length > 0 &&
      Object.keys(diastolicData).length > 0
          ? (
            <LineChart
              id='blood-pressure-control'
              width='550px'
              height='500px'
              option={ehrGraphOptions}
            />
          )
          : (
            <Empty
              description='Link your health record data'
              to='/my-care-network'
              buttonText='Link your health record data'
            />
          )}
      </>
    );

  return (
    <Container>
      <Title>Blood pressure control</Title>
      <Description
        style={{ marginBottom: '0' }}>
        High blood pressure affects 60-70% of ADPKD patients and begins early.
        Many times, this is the first sign of PKD. If not treated, it causes
        further damage to the kidneys, enlarges and thickens the heart muscle,
        and increases the risk for strokes and other cardiovascular events.
      </Description>
      {content}
    </Container>
  );
};

export default BloodPressureControl;
